import type { WikiItem, WikiVehiclePort } from "~~/types/gamedata";
import type { ShipexkursIconName } from "~/utils/shipexkurs-icons";

export interface ComponentGroup {
  superGroup: string;
  subGroup: string;
  icon: ShipexkursIconName;
}

/** Fest kuratierte Zuordnung `category_label` (Englisch, von der API) → Unterkategorie. */
const CATEGORY_MAP: Record<string, ComponentGroup> = {
  Radars: { superGroup: "Avionik", subGroup: "Radar", icon: "radar" },
  Scanners: { superGroup: "Avionik", subGroup: "Scanner", icon: "scanner" },
  "AI Modules": { superGroup: "Avionik", subGroup: "AI-Module", icon: "computer" },
  "Flight Controller": { superGroup: "Avionik", subGroup: "Flight Blades", icon: "computer" },
  Coolers: { superGroup: "Kühlsystem", subGroup: "Kühler", icon: "cooler" },
  "Life Support": { superGroup: "Lebenserhaltung", subGroup: "Lebenserhaltungssysteme", icon: "lifesupport" },
  "Power Plants": { superGroup: "Energie", subGroup: "Energiegenerator", icon: "powerplant" },
  Shields: { superGroup: "Schilde", subGroup: "Schildgeneratoren", icon: "shieldgenerator" },
  "Cargo Grids": { superGroup: "Sonstige", subGroup: "Frachtraster", icon: "cargogrid" },
  "Quantum Drives": { superGroup: "Antrieb", subGroup: "Quantenantriebe & Sprungmodule", icon: "quantumdrive" },
  "Missile & Bomb Racks": { superGroup: "Waffen", subGroup: "Raketenträger", icon: "missilerack" },
  Weapons: { superGroup: "Waffen", subGroup: "Schiffswaffen", icon: "weapon" },
  Turrets: { superGroup: "Turrets", subGroup: "Waffenhalterungen", icon: "turret" },
  "Remote Turrets": { superGroup: "Turrets", subGroup: "Fernbedienbare Türme", icon: "turret" },
  "Manned Turrets": { superGroup: "Turrets", subGroup: "Bemannte Türme", icon: "turret" },
  Armor: { superGroup: "Verteidigung", subGroup: "Panzerung", icon: "shieldgenerator" },
  "Counter Measures": { superGroup: "Verteidigung", subGroup: "Gegenmaßnahmen", icon: "utility" },
  "PDC Turrets": { superGroup: "Turrets", subGroup: "Point-Defense-Geschütze", icon: "turret" },
  QED: { superGroup: "QED", subGroup: "Quanteninterdiktion", icon: "qed" },
  EMP: { superGroup: "EMP", subGroup: "EMP-Generatoren", icon: "emp" },
  "Tractor Beams": { superGroup: "Utility", subGroup: "Tractor Beams", icon: "tractorbeam" },
};

/**
 * Fallback für Ausrüstung, die unter einem namenlosen Strukturport verschachtelt
 * ist (z.B. Hull C: Kühler, zweites Power Plant und Quantenantrieb hängen unter
 * `hardpoint_body_int_rear`, einem "Room"-Port ohne eigene `category_label`)
 * — dort liefert die API `category_label: null` auch am Ausrüstungs-Port
 * selbst, nur der Item-`type` bleibt verlässlich.
 */
const TYPE_FALLBACK_MAP: Record<string, ComponentGroup> = {
  PowerPlant: CATEGORY_MAP["Power Plants"],
  Cooler: CATEGORY_MAP["Coolers"],
  QuantumDrive: CATEGORY_MAP["Quantum Drives"],
  Shield: CATEGORY_MAP["Shields"],
};

const UTILITY_GROUPS = {
  tractor: { superGroup: "Utility", subGroup: "Tractor Beams", icon: "tractorbeam" },
  salvage: { superGroup: "Utility", subGroup: "Salvage", icon: "utility" },
  mining: { superGroup: "Utility", subGroup: "Mining", icon: "mininglaser" },
} satisfies Record<string, ComponentGroup>;

const COMPUTER_GROUP: ComponentGroup = {
  superGroup: "Avionik",
  subGroup: "Computer",
  icon: "computer",
};

const BATTERY_GROUP: ComponentGroup = {
  superGroup: "Energie",
  subGroup: "Batterien",
  icon: "battery",
};

const SCANNER_GROUP: ComponentGroup = {
  superGroup: "Avionik",
  subGroup: "Scanner",
  icon: "scanner",
};

const GRAVITY_GROUP: ComponentGroup = {
  superGroup: "Lebenserhaltung",
  subGroup: "Gravitationsgeneratoren",
  icon: "gravitygenerator",
};

const THRUSTER_ROLE_MAP: Record<string, ComponentGroup> = {
  Main: { superGroup: "Triebwerk", subGroup: "Haupttriebwerke", icon: "mainthruster" },
  Retro: { superGroup: "Triebwerk", subGroup: "Retro-Triebwerke", icon: "maneuveringthruster" },
  VTOL: { superGroup: "Triebwerk", subGroup: "VTOL-Triebwerke", icon: "maneuveringthruster" },
};
const MANEUVER_THRUSTER_GROUP: ComponentGroup = {
  superGroup: "Triebwerk",
  subGroup: "Manövriertriebwerke",
  icon: "maneuveringthruster",
};

// Eigenes Supergroup "Treibstoff" statt Unterordnung unter "Antrieb" — bündelt
// alles rund um Lagerung/Transfer von Treibstoff (Tanks, Einlässe, Betankung)
// getrennt von den Quantenantrieben, die inhaltlich zu Triebwerken/Navigation
// gehören (siehe `COMPONENT_COLUMNS`).
const FUEL_INTAKE_GROUP: ComponentGroup = {
  superGroup: "Treibstoff",
  subGroup: "Treibstoffeinlässe",
  icon: "fuelintake",
};
const FUEL_TANK_GROUP: ComponentGroup = {
  superGroup: "Treibstoff",
  subGroup: "Treibstofftanks (Hydrogen)",
  icon: "fueltank",
};
const QUANTUM_FUEL_TANK_GROUP: ComponentGroup = {
  superGroup: "Treibstoff",
  subGroup: "Quantentreibstofftanks",
  icon: "quantumfueltank",
};
/** Externe Betankungs-Container (z.B. Starfarers CR-60-Pods) — eigener Item-Typ `ExternalFuelTank`, `category_label: "Other"`. */
const EXTERNAL_FUEL_TANK_GROUP: ComponentGroup = {
  superGroup: "Treibstoff",
  subGroup: "Externe Treibstoffcontainer",
  icon: "container",
};

const MINING_POD_GROUP: ComponentGroup = {
  superGroup: "Sonstige",
  subGroup: "Erzbehälter",
  icon: "container",
};

const REFUEL_GROUP: ComponentGroup = {
  superGroup: "Treibstoff",
  subGroup: "Betankung",
  icon: "fuelnozzle",
};

/**
 * Betankungs-Arme (z.B. Starfarer `hardpoint_refuel_arm`) tragen
 * `category_label: "Mining & Salvage"` (dieselbe generische Toolarm-Kategorie
 * wie Tractor Beams/Mining) — nicht zuverlässig von `getUtilityGroup()`
 * unterscheidbar, da weder Item-Name noch -Typ "refuel" enthalten (das steckt
 * nur in der Fuel-Nozzle, die als Sub-Port *unter* dem Arm sitzt). Der
 * Port-Name selbst ist das einzige verlässliche Merkmal.
 */
function isRefuelPort(port: WikiVehiclePort): boolean {
  // Strom-/Datenrelais mit "refuel(l)ing" im Namen (z.B. Beleuchtung am
  // Betankungs-Steg) sind keine Betankungs-Ausrüstung, nur zufällig benannt.
  return port.category_label !== "Relays" && /refuel/i.test(port.name);
}

/** Reihenfolge + Zusammensetzung der Spalten im Komponentenbaum. */
export const COMPONENT_COLUMNS: string[][] = [
  ["Avionik", "Energie", "Kühlsystem", "Sonstige"],
  ["Antrieb", "Treibstoff", "Triebwerk", "Lebenserhaltung"],
  ["Schilde", "Waffen", "Turrets", "Verteidigung", "QED", "EMP", "Utility"],
];

interface EquipmentPortLike {
  name?: string | null;
  compatible_types?: Array<{ type: string; sub_types?: string[] | null }> | null;
  equipped_item?: {
    name?: string | null;
    type?: string | null;
    sub_type?: string | null;
  } | null;
  ports?: EquipmentPortLike[] | null;
}

/**
 * Utility-Werkzeuge sitzen je nach Schiff direkt an einem Port oder unter
 * einem bemannten/ferngesteuerten Turret. Deshalb wird der ganze Port-Baum
 * betrachtet, bevor das Turret als Waffen- oder Arbeits-Turret einsortiert wird.
 */
function getUtilityGroup(port: WikiVehiclePort, item: WikiItem | undefined): ComponentGroup | null {
  const types = new Set<string>();
  const names = new Set<string>();

  function collect(node: EquipmentPortLike): void {
    if (node.name) names.add(node.name.toLowerCase());
    for (const compatible of node.compatible_types ?? []) {
      types.add(compatible.type);
      for (const subType of compatible.sub_types ?? []) types.add(subType);
    }

    const equipped = node.equipped_item;
    if (equipped?.type) types.add(equipped.type);
    if (equipped?.sub_type) types.add(equipped.sub_type);
    if (equipped?.name) names.add(equipped.name.toLowerCase());

    for (const child of node.ports ?? []) collect(child);
  }

  collect(port);
  if (item) {
    if (item.type) types.add(item.type);
    if (item.sub_type) types.add(item.sub_type);
    if (item.name) names.add(item.name.toLowerCase());
    for (const child of item.ports ?? []) collect(child);
  }

  if (types.has("TractorBeam") || types.has("TowingBeam")) return UTILITY_GROUPS.tractor;
  if ([...types].some((type) => type === "SalvageHead" || type === "SalvageFieldEmitter")) {
    return UTILITY_GROUPS.salvage;
  }
  if ([...types].some((type) => type === "WeaponMining" || type === "MiningLaser")) {
    return UTILITY_GROUPS.mining;
  }

  // Bei älteren Datensätzen sind die verschachtelten Typen teilweise leer;
  // die kuratierte Kategorie plus Port-/Itemname bleibt dann der Rückfall.
  if (port.category_label === "Mining & Salvage") {
    if ([...names].some((name) => name.includes("salvage"))) return UTILITY_GROUPS.salvage;
    if ([...names].some((name) => name.includes("mining"))) return UTILITY_GROUPS.mining;
  }

  return null;
}

/**
 * Erkennt eine reine Piloten-Gimbal-Halterung ohne eigenes Turm-Gehäuse
 * (Markenname enthält immer "Gimbal Mount", z.B. "VariPuck S4 Gimbal
 * Mount") — im Gegensatz zu echten Türmen, die trotzdem `category_label:
 * "Turrets"` tragen können (z.B. der Heck-Kugelturm der Anvil Lightning F8C,
 * "Anvil Lightning F8C Turret", `sub_type: "BallTurret"`). `category_label`
 * und `sub_type` unterscheiden das nicht zuverlässig — beide Fälle können
 * `"Turrets"`/`"GunTurret"` tragen (verifiziert u.a. beim Redeemer, dessen
 * "Remote Turret"-Gehäuse ebenfalls `sub_type: "GunTurret"` trägt) — nur der
 * Item-Name ist konsistent.
 */
export function isBareGimbalMount(port: WikiVehiclePort): boolean {
  return port.category_label === "Turrets" && !!port.equipped_item?.name?.includes("Gimbal Mount");
}

/**
 * Computer-Slots liefert die Wiki-API derzeit als leere Ports ohne
 * `compatible_types` und ohne `equipped_item`. Der interne Portname ist damit
 * das einzige verlässliche Merkmal — `category_label` schwankt je nach Schiff
 * zwischen `"Systems"` und `"Other"` (verifiziert u.a. bei Carrack vs.
 * Cutlass Black/100i), deshalb beide zulassen statt nur `"Systems"`.
 */
export function isComputerPort(port: WikiVehiclePort): boolean {
  const category = port.category_label;
  return (category === "Systems" || category === "Other") && /(^|_)computer(_|$)/i.test(port.name);
}

/** Aktive Erz-Pods; `hardpoint_stored_pod_*` sind leere Reservehalterungen. */
export function isMiningPodPort(port: WikiVehiclePort): boolean {
  return /^hardpoint_mining_pod_/i.test(port.name)
    && port.equipped_item?.type === "Container"
    && port.equipped_item.sub_type === "Cargo";
}

/**
 * Ordnet einen Fahrzeug-Port der FleetYards-artigen Super-/Unterkategorie zu
 * (z.B. "Schilde" → "Schildgeneratoren"). `category_label` allein reicht bei
 * "Fuel" (Tanks vs. Einlässe) und "Thrusters" (Haupt-/Retro-/Manövrier-/VTOL)
 * nicht aus — dafür zusätzlich `compatible_types`/das aufgelöste Item.
 *
 * `null`, wenn die Kategorie bewusst nicht im Komponentenbaum gezeigt wird
 * (z.B. Türen) — diese Ports sind zwar teils `editable: true` in der API,
 * aber keine sinnvolle Loadout-Auswahl für die Anzeige.
 */
export function getComponentGroup(port: WikiVehiclePort, item: WikiItem | undefined): ComponentGroup | null {
  const category = port.category_label ?? "";
  const types = (port.compatible_types ?? []).map((entry) => entry.type);
  const equippedType = port.equipped_item?.type ?? item?.type;

  if (equippedType) types.push(equippedType);

  if (isComputerPort(port)) return COMPUTER_GROUP;
  if (isMiningPodPort(port)) return MINING_POD_GROUP;
  if (types.includes("Battery")) return BATTERY_GROUP;
  if (types.includes("Scanner")) return SCANNER_GROUP;
  if (types.includes("GravityGenerator")) return GRAVITY_GROUP;
  if (isRefuelPort(port)) return REFUEL_GROUP;
  // Externe Betankungs-Container (z.B. Starfarer-Fuel-Pods) tragen
  // `category_label: "Other"` statt "Fuel" — nur der Item-Typ ist eindeutig.
  if (types.includes("ExternalFuelTank")) return EXTERNAL_FUEL_TANK_GROUP;

  const utilityGroup = getUtilityGroup(port, item);
  if (utilityGroup) return utilityGroup;

  if (category === "Fuel") {
    if (types.includes("FuelIntake")) return FUEL_INTAKE_GROUP;
    // Quanten- und Hydrogen-Tanks teilen sich `category_label: "Fuel"` — nur
    // `compatible_types` (bzw. der Item-`type`) unterscheidet sie.
    return types.includes("QuantumFuelTank") ? QUANTUM_FUEL_TANK_GROUP : FUEL_TANK_GROUP;
  }

  if (category === "Thrusters") {
    const role = item?.thruster?.role;
    return (role && THRUSTER_ROLE_MAP[role]) || MANEUVER_THRUSTER_GROUP;
  }

  if (category === "Turrets" && port.equipped_item) {
    // Reine Piloten-Gimbal-Halterungen an einem Hardpoint (`isBareGimbalMount()`)
    // sind keine Türme, auch wenn die Wiki-API sie pauschal als "Turrets"
    // taggt — inhaltlich normale Schiffswaffen mit Gimbal-Unterstützung.
    if (isBareGimbalMount(port)) return CATEGORY_MAP["Weapons"];
    // Türme mit eigenem Gehäuse, die die API trotzdem nur pauschal als
    // "Turrets" statt "Remote Turrets" taggt — gehören zu den echten Türmen,
    // nicht zu den reinen Waffenhalterungen.
    return CATEGORY_MAP["Remote Turrets"];
  }

  if (CATEGORY_MAP[category]) return CATEGORY_MAP[category];
  // Kein `category_label` (verschachtelt unter einem Strukturport, siehe
  // `TYPE_FALLBACK_MAP`) — über den Item-Typ selbst einordnen.
  return (equippedType && TYPE_FALLBACK_MAP[equippedType]) ?? null;
}
